import React, {useEffect, useRef, useState} from 'react';
import {
  ActivityIndicator,
  Alert,
  Animated,
  Easing,
  KeyboardAvoidingView,
  Platform,
  Pressable,
  ScrollView,
  StatusBar,
  StyleSheet,
  Text,
  TextInput,
  useWindowDimensions,
  View,
} from 'react-native';
import {getItem, setItem, removeItem} from '../utils/storage';
import {SERVER_URL} from '../config';

const palette = {
  ink: '#100B25',
  panel: '#211747',
  panelLight: '#2A1E58',
  purple: '#7C4DFF',
  pink: '#FF4D8D',
  yellow: '#FFD65A',
  cyan: '#55E6D5',
  white: '#FFFFFF',
  muted: '#B7AED3',
};

function FloatingNote({symbol, style, delay = 0}: any) {
  const motion = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    Animated.loop(
      Animated.sequence([
        Animated.delay(delay),
        Animated.timing(motion, {toValue: 1, duration: 2600, easing: Easing.inOut(Easing.sin), useNativeDriver: true}),
        Animated.timing(motion, {toValue: 0, duration: 2600, easing: Easing.inOut(Easing.sin), useNativeDriver: true}),
      ]),
    ).start();
  }, []);

  return (
    <Animated.Text
      style={[
        styles.floatingNote,
        style,
        {transform: [{translateY: motion.interpolate({inputRange: [0, 1], outputRange: [0, -16]})}, {rotate: motion.interpolate({inputRange: [0, 1], outputRange: ['-8deg', '8deg']})}]},
      ]}>
      {symbol}
    </Animated.Text>
  );
}

function GameButton({label, icon, onPress, loading, secondary = false}: any) {
  const scale = useRef(new Animated.Value(1)).current;
  return (
    <Pressable
      disabled={loading}
      onPress={onPress}
      onPressIn={() => Animated.spring(scale, {toValue: 0.96, useNativeDriver: true}).start()}
      onPressOut={() => Animated.spring(scale, {toValue: 1, friction: 4, useNativeDriver: true}).start()}>
      <Animated.View style={[styles.gameButton, secondary && styles.gameButtonSecondary, {transform: [{scale}], opacity: loading ? 0.72 : 1}]}>
        <View style={[styles.buttonIcon, secondary && styles.buttonIconSecondary]}>
          <Text style={styles.buttonIconText}>{icon}</Text>
        </View>
        <Text style={[styles.gameButtonLabel, secondary && styles.gameButtonLabelSecondary]}>{label}</Text>
        {loading ? <ActivityIndicator color={secondary ? palette.cyan : palette.ink} /> : <Text style={[styles.arrow, secondary && {color: palette.cyan}]}>›</Text>}
      </Animated.View>
    </Pressable>
  );
}

export default function Home({navigation}: any) {
  const [name, setName] = useState('');
  const [nameError, setNameError] = useState<string | null>(null);
  const [user, setUser] = useState<any>(null);
  const [joinCode, setJoinCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [showJoin, setShowJoin] = useState(false);
  const {width} = useWindowDimensions();
  const isWide = width >= 760;
  const entrance = useRef(new Animated.Value(0)).current;
  const pulse = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    (async () => {
      const raw = await getItem('user');
      if (raw) setUser(JSON.parse(raw));
    })();
    Animated.timing(entrance, {toValue: 1, duration: 700, easing: Easing.out(Easing.cubic), useNativeDriver: true}).start();
    Animated.loop(
      Animated.sequence([
        Animated.timing(pulse, {toValue: 1, duration: 1500, easing: Easing.inOut(Easing.ease), useNativeDriver: true}),
        Animated.timing(pulse, {toValue: 0, duration: 1500, easing: Easing.inOut(Easing.ease), useNativeDriver: true}),
      ]),
    ).start();
  }, []);

  async function saveName() {
    setNameError(null);
    if (!name.trim()) { setNameError('Choisis un pseudo pour continuer.'); return; }
    try {
      setLoading(true);
      const res = await fetch(`${SERVER_URL}/api/users`, {method: 'POST', headers: {'Content-Type': 'application/json'}, body: JSON.stringify({name: name.trim()})});
      const j = await res.json();
      if (!res.ok) {
        if (res.status === 409) {
          setNameError('Ce pseudo est déjà utilisé. Choisis un autre pseudo.');
          return;
        }
        return Alert.alert('Erreur', j.error || JSON.stringify(j));
      }
      setUser(j);
      await setItem('user', JSON.stringify(j));
    } catch (e: any) {
      Alert.alert('Serveur inaccessible', e?.message || String(e));
    } finally {
      setLoading(false);
    }
  }

  async function ensureUser() {
    if (user) return user;
    const raw = await getItem('user');
    if (raw) {
      const saved = JSON.parse(raw);
      setUser(saved);
      return saved;
    }
    return null;
  }

  async function createRoom() {
    setCreateError(null);
    setCreateLoading(true);
    try {
      const u = await ensureUser();
      if (!u) return Alert.alert('Erreur', "Identifie-toi d'abord");
      const res = await fetch(`${SERVER_URL}/api/rooms`, {method: 'POST', headers: {'Content-Type': 'application/json'}, body: JSON.stringify({hostId: u.id})});
      const j = await res.json();
      if (!res.ok) {
        setCreateError(j?.error || 'Impossible de créer la salle');
        return Alert.alert('Erreur', j?.error || 'Impossible de créer la salle');
      }
      navigation.navigate('RoomLobby', {code: j.code, user: u, room: j});
    } catch (e: any) {
      setCreateError(e?.message || String(e));
      Alert.alert('Erreur réseau', e?.message || String(e));
    } finally {
      setCreateLoading(false);
    }
  }

  async function joinRoom() {
    const u = await ensureUser();
    const code = joinCode.trim().toUpperCase();
    if (!u) return Alert.alert('Erreur', "Identifie-toi d'abord");
    if (!code) return Alert.alert('Code manquant', 'Saisis le code affiché par le maître du jeu.');
    try {
      setLoading(true);
      const res = await fetch(`${SERVER_URL}/api/rooms/${code}/join`, {method: 'POST', headers: {'Content-Type': 'application/json'}, body: JSON.stringify({userId: u.id})});
      const j = await res.json();
      if (!res.ok) return Alert.alert('Salle introuvable', j.error || 'Vérifie le code et réessaie.');
      navigation.navigate('RoomLobby', {code, user: u, room: j, joinByCode: true});
    } catch (e: any) {
      Alert.alert('Erreur réseau', e?.message || String(e));
    } finally {
      setLoading(false);
    }
  }

  async function logout() {
    await removeItem('user');
    setUser(null);
    setName('');
    setJoinCode('');
    setShowJoin(false);
    navigation.reset({index:0,routes:[{name:'Login'}]});
  }

  const animatedEntrance = {
    opacity: entrance,
    transform: [{translateY: entrance.interpolate({inputRange: [0, 1], outputRange: [28, 0]})}],
  };

  return (
    <View style={styles.screen}>
      <StatusBar barStyle="light-content" backgroundColor={palette.ink} />
      <View style={styles.glowPink} />
      <View style={styles.glowPurple} />
      <FloatingNote symbol="♪" style={styles.noteOne} />
      <FloatingNote symbol="♫" style={styles.noteTwo} delay={500} />
      <FloatingNote symbol="★" style={styles.noteThree} delay={900} />

      <KeyboardAvoidingView style={styles.flex} behavior={Platform.OS === 'ios' ? 'padding' : undefined}>
        <ScrollView scrollEnabled={false} contentContainerStyle={[styles.scrollContent,!isWide&&styles.scrollContentMobile]} keyboardShouldPersistTaps="handled" showsVerticalScrollIndicator={false}>
          <Animated.View style={[styles.page, isWide ? styles.pageWide : styles.pageMobile, animatedEntrance]}>
            <View style={[styles.hero, isWide ? styles.heroWide : styles.heroMobile]}>
              <View style={[styles.brandRow,!isWide&&styles.brandRowMobile]}>
                <Animated.View style={[styles.logoDisc,!isWide&&styles.logoDiscMobile,{transform: [{scale: pulse.interpolate({inputRange: [0, 1], outputRange: [1, 1.04]})}]}]}>
                  <View style={styles.discRing} />
                  <View style={styles.discCenter}><Text style={styles.discNote}>♪</Text></View>
                </Animated.View>
                <View>
                  <Text style={styles.eyebrow}>LE BLIND TEST CHRONOLOGIQUE</Text>
                  <Text style={[styles.logo,!isWide&&styles.logoMobile]}>SONG<Text style={styles.logoAccent}>ORIA</Text></Text>
                </View>
              </View>
              <Text style={[styles.heroTitle,!isWide&&styles.heroTitleMobile]}>Écoute. Devine.{isWide?`\n`:' '}Place le hit.</Text>
              <Text style={[styles.heroText,!isWide&&styles.heroTextMobile]}>Défie tes amis et reconstruis la timeline musicale parfaite, morceau après morceau.</Text>
              <View style={[styles.statRow,!isWide&&styles.statRowMobile]}>
                <View style={[styles.stat,!isWide&&styles.statMobile]}><Text style={styles.statValue}>138</Text><Text style={styles.statLabel}>HITS</Text></View>
                <View style={styles.statDivider} />
                <View style={[styles.stat,!isWide&&styles.statMobile]}><Text style={styles.statValue}>2–4</Text><Text style={styles.statLabel}>ÉQUIPES</Text></View>
                <View style={styles.statDivider} />
                <View style={[styles.stat,!isWide&&styles.statMobile]}><Text style={styles.statValue}>∞</Text><Text style={styles.statLabel}>AMBIANCE</Text></View>
              </View>
            </View>

            <View style={[styles.lobbyCard, isWide ? styles.lobbyCardWide : styles.lobbyCardMobile]}>
              {!user ? (
                <View>
                  <View style={styles.stepBadge}><Text style={styles.stepBadgeText}>1</Text></View>
                  <Text style={styles.cardKicker}>AVANT DE JOUER</Text>
                  <Text style={styles.cardTitle}>Choisis ton nom de scène</Text>
                  <Text style={styles.cardDescription}>C’est le nom que verront les autres joueurs.</Text>
                  <View style={styles.inputShell}>
                    <Text style={styles.inputIcon}>⚡</Text>
                    <TextInput
                      style={styles.input}
                      placeholder="Ton pseudo"
                      placeholderTextColor="#7F749F"
                      value={name}
                      onChangeText={(value)=>{setName(value);if(nameError)setNameError(null);}}
                      onSubmitEditing={saveName}
                      maxLength={18}
                      autoCapitalize="words"
                    />
                    <Text style={styles.inputCount}>{name.length}/18</Text>
                  </View>
                  {nameError ? <View style={styles.nameError}><Text style={styles.nameErrorIcon}>!</Text><Text style={styles.nameErrorText}>{nameError}</Text></View> : null}
                  <GameButton label={loading ? 'ENTRÉE EN SCÈNE…' : 'ENTRER DANS LE JEU'} icon="▶" onPress={saveName} loading={loading} />
                </View>
              ) : (
                <View>
                  <View style={styles.playerRow}>
                    <View style={styles.avatar}><Text style={styles.avatarText}>{user.name?.slice(0, 1).toUpperCase()}</Text><View style={styles.onlineDot} /></View>
                    <View style={styles.playerCopy}>
                      <Text style={styles.welcome}>PRÊT À JOUER</Text>
                      <Text style={styles.playerName}>Salut, {user.name} !</Text>
                    </View>
                    <Pressable onPress={logout} style={styles.logoutButton} accessibilityLabel="Se déconnecter">
                      <Text style={styles.logoutIcon}>↪</Text><Text style={styles.logoutText}>QUITTER</Text>
                    </Pressable>
                  </View>

                  <Text style={styles.sectionLabel}>LANCE LA PROCHAINE PARTIE</Text>
                  <GameButton label="CRÉER UNE SALLE" icon="＋" onPress={createRoom} loading={createLoading} />
                  <View style={styles.orRow}><View style={styles.orLine} /><Text style={styles.orText}>OU</Text><View style={styles.orLine} /></View>

                  {!showJoin ? (
                    <GameButton label="REJOINDRE UNE SALLE" icon="#" onPress={() => setShowJoin(true)} secondary />
                  ) : (
                    <Animated.View style={styles.joinPanel}>
                      <Text style={styles.joinTitle}>Code de la salle</Text>
                      <View style={[styles.codeRow,!isWide&&styles.codeRowMobile]}>
                        <TextInput
                          style={[styles.codeInput,!isWide&&styles.codeInputMobile]}
                          placeholder="ABC123"
                          placeholderTextColor="#675D85"
                          value={joinCode}
                          onChangeText={value => setJoinCode(value.toUpperCase().replace(/[^A-Z0-9]/g, ''))}
                          maxLength={6}
                          autoCapitalize="characters"
                          autoFocus
                          onSubmitEditing={joinRoom}
                        />
                        <Pressable style={[styles.joinAction,!isWide&&styles.joinActionMobile]} onPress={joinRoom} disabled={loading}>
                          {loading ? <ActivityIndicator color={palette.ink} /> : <Text style={styles.joinActionText}>GO</Text>}
                        </Pressable>
                      </View>
                      <Pressable onPress={() => setShowJoin(false)}><Text style={styles.cancelText}>Annuler</Text></Pressable>
                    </Animated.View>
                  )}
                  {createError ? <Text style={styles.error}>{createError}</Text> : null}
                </View>
              )}
            </View>
          </Animated.View>

          <View style={styles.footer}><View style={styles.liveDot} /><Text style={styles.footerText}>SERVEUR EN LIGNE  •  VERSION PARTY</Text></View>
        </ScrollView>
      </KeyboardAvoidingView>
    </View>
  );
}

const styles = StyleSheet.create({
  flex: {flex: 1},
  screen: {flex: 1, backgroundColor: palette.ink, overflow: 'hidden'},
  scrollContent: {flexGrow: 1, justifyContent: 'center', paddingHorizontal: 20, paddingVertical: 34},
  scrollContentMobile:{paddingHorizontal:10,paddingVertical:8},
  page: {width: '100%', maxWidth: 540, alignSelf: 'center'},
  pageMobile:{maxWidth:440},
  pageWide: {maxWidth: 1040, flexDirection: 'row', alignItems: 'center', gap: 70},
  hero: {marginBottom: 30},
  heroMobile:{marginBottom:8},
  heroWide: {flex: 1, marginBottom: 0},
  brandRow: {flexDirection: 'row', alignItems: 'center', marginBottom: 28},
  brandRowMobile:{justifyContent:'center',marginBottom:4},
  logoDisc: {width: 58, height: 58, borderRadius: 29, backgroundColor: '#080515', marginRight: 14, justifyContent: 'center', alignItems: 'center', borderWidth: 2, borderColor: palette.pink},
  logoDiscMobile:{width:38,height:38,borderRadius:19,marginRight:9},
  discRing: {position: 'absolute', width: 38, height: 38, borderRadius: 19, borderWidth: 1, borderColor: '#5D3158'},
  discCenter: {width: 22, height: 22, borderRadius: 11, backgroundColor: palette.yellow, justifyContent: 'center', alignItems: 'center'},
  discNote: {color: palette.ink, fontSize: 14, fontWeight: '900'},
  eyebrow: {color: palette.cyan, fontSize: 10, letterSpacing: 1.8, fontWeight: '800', marginBottom: 2},
  logo: {color: palette.white, fontSize: 34, letterSpacing: -1.5, fontWeight: '900'},
  logoMobile:{fontSize:25},
  logoAccent: {color: palette.pink},
  heroTitle: {color: palette.white, fontSize: 42, lineHeight: 45, letterSpacing: -1.5, fontWeight: '900', marginBottom: 16},
  heroTitleMobile:{fontSize:18,lineHeight:21,letterSpacing:-.5,marginBottom:3,textAlign:'center'},
  heroText: {color: palette.muted, fontSize: 16, lineHeight: 24, maxWidth: 470},
  heroTextMobile:{fontSize:10,lineHeight:13,textAlign:'center',alignSelf:'center'},
  statRow: {flexDirection: 'row', alignItems: 'center', marginTop: 26, backgroundColor: 'rgba(255,255,255,0.04)', borderRadius: 18, paddingVertical: 14, paddingHorizontal: 8, alignSelf: 'flex-start'},
  statRowMobile:{width:'100%',alignSelf:'stretch',justifyContent:'space-around',marginTop:5,paddingVertical:5,borderRadius:12},
  stat: {paddingHorizontal: 14, alignItems: 'center'},
  statMobile:{flex:1,paddingHorizontal:3},
  statValue: {color: palette.yellow, fontSize: 18, fontWeight: '900'},
  statLabel: {color: palette.muted, fontSize: 8, letterSpacing: 1.2, fontWeight: '800', marginTop: 2},
  statDivider: {width: 1, height: 25, backgroundColor: '#463A67'},
  lobbyCard: {backgroundColor: palette.panel, borderRadius: 28, padding: 24, borderWidth: 1, borderColor: '#3B2D6A', shadowColor: palette.purple, shadowOffset: {width: 0, height: 18}, shadowOpacity: 0.28, shadowRadius: 30, elevation: 14},
  lobbyCardMobile:{borderRadius:20,padding:14},
  lobbyCardWide: {width: 420},
  stepBadge: {width: 34, height: 34, borderRadius: 17, backgroundColor: palette.pink, justifyContent: 'center', alignItems: 'center', marginBottom: 16},
  stepBadgeText: {color: palette.white, fontWeight: '900'},
  cardKicker: {color: palette.cyan, fontSize: 10, letterSpacing: 1.7, fontWeight: '900', marginBottom: 7},
  cardTitle: {color: palette.white, fontSize: 25, lineHeight: 30, fontWeight: '900'},
  cardDescription: {color: palette.muted, fontSize: 14, lineHeight: 20, marginTop: 7, marginBottom: 20},
  inputShell: {height: 58, borderRadius: 16, backgroundColor: '#17102F', borderWidth: 1, borderColor: '#493B73', flexDirection: 'row', alignItems: 'center', paddingHorizontal: 15, marginBottom: 14},
  inputIcon: {fontSize: 17, marginRight: 10},
  input: {flex: 1, color: palette.white, fontSize: 16, fontWeight: '700', outlineStyle: 'none'} as any,
  inputCount: {color: '#756A92', fontSize: 11},
  nameError:{flexDirection:'row',alignItems:'center',backgroundColor:'rgba(255,77,109,.12)',borderWidth:1,borderColor:'rgba(255,77,109,.45)',borderRadius:12,paddingHorizontal:10,paddingVertical:8,marginTop:-5,marginBottom:12},
  nameErrorIcon:{width:19,height:19,borderRadius:10,backgroundColor:'#FF4D6D',color:'#FFF',fontSize:11,fontWeight:'900',textAlign:'center',lineHeight:19,marginRight:8},
  nameErrorText:{flex:1,color:'#FF9BAE',fontSize:11,fontWeight:'800'},
  gameButton: {minHeight: 62, borderRadius: 17, backgroundColor: palette.yellow, flexDirection: 'row', alignItems: 'center', padding: 9},
  gameButtonSecondary: {backgroundColor: '#251B4B', borderWidth: 1, borderColor: '#4E4076'},
  buttonIcon: {width: 43, height: 43, borderRadius: 13, backgroundColor: 'rgba(16,11,37,0.12)', alignItems: 'center', justifyContent: 'center'},
  buttonIconSecondary: {backgroundColor: 'rgba(85,230,213,0.10)'},
  buttonIconText: {fontSize: 22, fontWeight: '900', color: palette.ink},
  gameButtonLabel: {flex: 1, color: palette.ink, fontSize: 14, letterSpacing: 0.7, fontWeight: '900', marginLeft: 13},
  gameButtonLabelSecondary: {color: palette.white},
  arrow: {color: palette.ink, fontSize: 30, fontWeight: '500', marginRight: 10, marginBottom: 3},
  playerRow: {flexDirection: 'row', alignItems: 'center', marginBottom: 26},
  avatar: {width: 54, height: 54, borderRadius: 18, backgroundColor: palette.purple, alignItems: 'center', justifyContent: 'center', marginRight: 13},
  avatarText: {color: palette.white, fontSize: 23, fontWeight: '900'},
  onlineDot: {position: 'absolute', width: 13, height: 13, borderRadius: 7, right: -2, bottom: -2, backgroundColor: palette.cyan, borderWidth: 3, borderColor: palette.panel},
  playerCopy: {flex: 1},
  welcome: {color: palette.cyan, fontSize: 9, letterSpacing: 1.4, fontWeight: '900'},
  playerName: {color: palette.white, fontSize: 22, fontWeight: '900', marginTop: 2},
  levelPill: {backgroundColor: '#392758', paddingHorizontal: 12, paddingVertical: 7, borderRadius: 20, borderWidth: 1, borderColor: '#584276'},
  levelText: {color: palette.yellow, fontSize: 10, letterSpacing: 1, fontWeight: '900'},
  logoutButton: {height:38,borderRadius:13,borderWidth:1,borderColor:'#4D3D70',backgroundColor:'#2A1E50',paddingHorizontal:10,flexDirection:'row',alignItems:'center'},
  logoutIcon: {color:'#FF8FB8',fontSize:17,fontWeight:'900',marginRight:5,transform:[{rotate:'180deg'}]},
  logoutText: {color:'#D2C9E6',fontSize:8,fontWeight:'900',letterSpacing:.8},
  sectionLabel: {color: palette.muted, fontSize: 10, letterSpacing: 1.4, fontWeight: '800', marginBottom: 11},
  orRow: {flexDirection: 'row', alignItems: 'center', marginVertical: 15},
  orLine: {flex: 1, height: 1, backgroundColor: '#42365F'},
  orText: {color: '#7D7299', fontSize: 9, fontWeight: '900', marginHorizontal: 12},
  joinPanel: {width:'100%',maxWidth:'100%',minWidth:0,backgroundColor: '#17102F', borderRadius: 18, padding: 15, borderWidth: 1, borderColor: '#443769',boxSizing:'border-box'} as any,
  joinTitle: {color: palette.white, fontSize: 13, fontWeight: '800', marginBottom: 10},
  codeRow: {width:'100%',maxWidth:'100%',minWidth:0,flexDirection: 'row', gap: 8},
  codeRowMobile:{flexDirection:'column'},
  codeInput: {flex: 1, flexShrink:1,minWidth:0,width:1,height: 54, paddingHorizontal:8,borderRadius: 13, backgroundColor: '#0F0A20', color: palette.white, textAlign: 'center', fontSize: 20, fontWeight: '900', letterSpacing: 3, borderWidth: 1, borderColor: '#47396D', outlineStyle: 'none',boxSizing:'border-box'} as any,
  codeInputMobile:{flexGrow:0,flexBasis:54,width:'100%',maxWidth:'100%',alignSelf:'stretch'},
  joinAction: {width: 58,flexShrink:0,borderRadius: 13, backgroundColor: palette.cyan, alignItems: 'center', justifyContent: 'center'},
  joinActionMobile:{width:'100%',height:50,alignSelf:'stretch'},
  joinActionText: {color: palette.ink, fontSize: 15, fontWeight: '900'},
  cancelText: {color: palette.muted, fontSize: 12, textAlign: 'center', marginTop: 12},
  error: {color: '#FF8FB8', fontSize: 12, marginTop: 12, textAlign: 'center'},
  footer: {flexDirection: 'row', alignItems: 'center', justifyContent: 'center', marginTop: 28},
  liveDot: {width: 7, height: 7, borderRadius: 4, backgroundColor: palette.cyan, marginRight: 8},
  footerText: {color: '#62597A', fontSize: 9, letterSpacing: 1.4, fontWeight: '800'},
  floatingNote: {position: 'absolute', color: 'rgba(255,255,255,0.10)', fontSize: 48, fontWeight: '900'},
  noteOne: {top: '9%', left: '5%'},
  noteTwo: {bottom: '12%', right: '5%', fontSize: 64},
  noteThree: {top: '18%', right: '13%', color: 'rgba(255,214,90,0.08)', fontSize: 32},
  glowPink: {position: 'absolute', width: 360, height: 360, borderRadius: 180, backgroundColor: 'rgba(255,77,141,0.09)', top: -180, right: -110},
  glowPurple: {position: 'absolute', width: 430, height: 430, borderRadius: 215, backgroundColor: 'rgba(124,77,255,0.10)', bottom: -270, left: -180},
});
